// src/prototype/components/ea_reports/use_case/EAUseCasePrintStyles.ts
// ============================================================
// DVA EA USE CASE ARTIFACT — SHARED PRINT STYLES
// ============================================================
// Inject via <style dangerouslySetInnerHTML={{ __html: UA_PRINT_CSS }} />
// inside any artifact shell's <head> or as a sibling <style> block.
// ============================================================

export const UA_PRINT_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800;900&display=swap');

  /* ── Reset & Base ─────────────────────────────────────── */
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  html, body {
    font-family: 'Montserrat', sans-serif;
    font-size: 10pt;
    color: #1a1a2e;
    background: #ffffff;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  /* ── Page Layout ──────────────────────────────────────── */
  @page {
    size: letter portrait;
    margin: 0.65in 0.7in 0.75in 0.7in;
  }

  /* ── Watermark ────────────────────────────────────────── */
  .ua-watermark {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) rotate(-35deg);
    font-family: 'Montserrat', sans-serif;
    font-size: 72pt;
    font-weight: 900;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #18365E;
    opacity: 0.055;
    pointer-events: none;
    z-index: 0;
    white-space: nowrap;
    user-select: none;
  }

  /* ── Page Break Utilities ─────────────────────────────── */
  .ua-page-break-before { page-break-before: always; break-before: page; }
  .ua-page-break-after  { page-break-after: always;  break-after: page;  }
  .ua-avoid-break       { page-break-inside: avoid;  break-inside: avoid; }

  /* ── Cover Page ───────────────────────────────────────── */
  .ua-cover {
    min-height: 100vh;
    background: #18365E;
    display: flex;
    flex-direction: column;
    padding: 0;
    position: relative;
    overflow: hidden;
  }

  .ua-cover-accent {
    position: absolute;
    bottom: 0; right: 0;
    width: 320px; height: 320px;
    border-radius: 50%;
    background: #FF6E4C;
    opacity: 0.12;
    transform: translate(30%, 30%);
  }

  .ua-cover-accent-2 {
    position: absolute;
    top: -60px; left: -60px;
    width: 200px; height: 200px;
    border-radius: 50%;
    background: #ffffff;
    opacity: 0.04;
  }

  /* ── Header ───────────────────────────────────────────── */
  .ua-header {
    background: #18365E;
    color: #ffffff;
    padding: 28px 32px 24px;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    position: relative;
  }

  .ua-header-left { flex: 1; min-width: 0; }

  .ua-header-badge {
    display: inline-block;
    font-size: 7pt;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #FF6E4C;
    background: rgba(255,110,76,0.15);
    border: 1px solid rgba(255,110,76,0.35);
    padding: 3px 10px;
    border-radius: 20px;
    margin-bottom: 8px;
  }

  .ua-header-title {
    font-size: 18pt;
    font-weight: 800;
    color: #ffffff;
    line-height: 1.15;
    margin-bottom: 4px;
  }

  .ua-header-subtitle {
    font-size: 9.5pt;
    font-weight: 600;
    color: rgba(255,255,255,0.65);
    margin-bottom: 12px;
    letter-spacing: 0.02em;
  }

  .ua-header-use-case {
    font-size: 8.5pt;
    font-weight: 500;
    color: rgba(255,255,255,0.55);
    margin-top: 2px;
  }

  .ua-header-meta {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 6px;
    shrink: 0;
  }

  .ua-header-logo {
    height: 32px;
    width: auto;
    object-fit: contain;
    filter: brightness(0) invert(1);
    opacity: 0.9;
  }

  .ua-header-date {
    font-size: 7.5pt;
    font-weight: 600;
    color: rgba(255,255,255,0.5);
    letter-spacing: 0.06em;
  }

  .ua-header-client {
    font-size: 8pt;
    font-weight: 700;
    color: rgba(255,255,255,0.8);
    letter-spacing: 0.03em;
  }

  /* ── Summary Strip ────────────────────────────────────── */
  .ua-summary-strip {
    display: flex;
    background: #F5F7FA;
    border-bottom: 1px solid #e5e7eb;
    padding: 0;
    page-break-inside: avoid;
    break-inside: avoid;
  }

  .ua-summary-item {
    flex: 1;
    padding: 12px 18px;
    border-right: 1px solid #e5e7eb;
    position: relative;
  }

  .ua-summary-item:last-child { border-right: none; }

  .ua-summary-label {
    font-size: 6.5pt;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #717182;
    margin-bottom: 4px;
  }

  .ua-summary-value {
    font-size: 10pt;
    font-weight: 800;
    color: #18365E;
    line-height: 1.2;
  }

  .ua-summary-value.accent { color: #FF6E4C; }

  /* ── Section Block ────────────────────────────────────── */
  .ua-section {
    padding: 24px 32px;
    border-bottom: 1px solid #e5e7eb;
    position: relative;
    z-index: 1;
  }

  .ua-section:last-child { border-bottom: none; }

  .ua-section-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 14px;
    padding-bottom: 10px;
    border-bottom: 2px solid #F5F7FA;
    break-after: avoid;
    page-break-after: avoid;
  }

  .ua-section-icon {
    width: 28px;
    height: 28px;
    border-radius: 8px;
    background: #18365E;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .ua-section-number {
    font-size: 7pt;
    font-weight: 800;
    color: #ffffff;
  }

  .ua-section-title {
    font-size: 10pt;
    font-weight: 800;
    color: #18365E;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .ua-section-content {
    font-size: 9.5pt;
    font-weight: 400;
    color: #374151;
    line-height: 1.65;
  }

  .ua-section-content p { margin-bottom: 10px; }
  .ua-section-content p:last-child { margin-bottom: 0; }

  .ua-section-content ul {
    padding-left: 18px;
    margin-bottom: 10px;
  }

  .ua-section-content li {
    margin-bottom: 5px;
    line-height: 1.55;
  }

  /* ── Content Primitives ───────────────────────────────── */
  .ua-callout {
    background: #F5F7FA;
    border-left: 3px solid #FF6E4C;
    border-radius: 4px;
    padding: 12px 16px;
    margin: 12px 0;
    font-size: 9pt;
    color: #18365E;
    font-weight: 600;
    break-inside: avoid;
    page-break-inside: avoid;
  }

  .ua-metric-row {
    display: flex;
    gap: 12px;
    margin: 12px 0;
    flex-wrap: wrap;
  }

  .ua-metric-card {
    flex: 1;
    min-width: 100px;
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 12px 14px;
    text-align: center;
  }

  .ua-metric-card-label {
    font-size: 6.5pt;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #717182;
    margin-bottom: 5px;
  }

  .ua-metric-card-value {
    font-size: 13pt;
    font-weight: 800;
    color: #18365E;
  }

  .ua-risk-row {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 8px;
    margin: 4px 0;
    padding: 10px 12px;
    background: #F5F7FA;
    border-radius: 6px;
    border: 1px solid #e5e7eb;
  }

  .ua-risk-cell-label {
    font-size: 6.5pt;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #717182;
    margin-bottom: 3px;
  }

  .ua-risk-cell-value {
    font-size: 8.5pt;
    font-weight: 600;
    color: #18365E;
    line-height: 1.4;
  }

  /* ── Footer ───────────────────────────────────────────── */
  .ua-footer {
    position: fixed;
    bottom: 0;
    left: 0; right: 0;
    background: #ffffff;
    border-top: 1px solid #e5e7eb;
    padding: 6px 32px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 6.5pt;
    font-weight: 600;
    color: #717182;
    letter-spacing: 0.04em;
    z-index: 100;
  }

  .ua-footer-brand { color: #18365E; }
  .ua-footer-badge {
    color: #FF6E4C;
    font-weight: 700;
    font-size: 6pt;
    letter-spacing: 0.12em;
    text-transform: uppercase;
  }

  /* ── Print Overrides ──────────────────────────────────── */
  @media print {
    body { margin: 0; padding: 0; }

    .ua-no-print { display: none !important; }

    .ua-watermark {
      position: fixed;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    .ua-footer {
      position: fixed;
      bottom: 0;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    .ua-cover {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    .ua-header {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    .ua-summary-strip {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    table tr {
      break-inside: avoid;
      page-break-inside: avoid;
    }

    table thead {
      display: table-header-group;
    }
  }

  /* ── Screen Preview ───────────────────────────────────── */
  @media screen {
    body {
      background: #e5e7eb;
      padding: 32px;
    }

    .ua-page {
      background: #ffffff;
      max-width: 816px;
      margin: 0 auto 32px;
      box-shadow: 0 4px 24px rgba(0,0,0,0.12);
      border-radius: 4px;
      overflow: hidden;
    }
  }
`;